// frontend/src/components/jobPlacement/JobPlacementForm.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import SignatureField from "../SignatureField";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";
import "../../assets/css/signature.css";

const today = new Date().toISOString().split("T")[0];

const initialFormState = {
  consumer_id: "",
  date: today,
  skills: "",
  vocational_goal: "",
  consumer_input: "",
  counselor_input: "",
  consumer_signature_date: today,
  crp_signature_date: today,
  wvdrs_signature_date: today,
  job_titles: [{ job_title: "", availability_in_community: "" }],
  support_needs: [""],
  job_search_notes: [""],
  job_placement_notes: [""],
};

const JobPlacementForm = ({ plan, onCancel, onSave }) => {
  const { user, tenant_id: tenantId } = useContext(AuthContext);
  const apiBase = getApiBaseURL();
  const userId = user?.user_id;

  const [consumers, setConsumers] = useState([]);
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showSignatures, setShowSignatures] = useState(false);

  // Signature states - DON'T initialize with plan data
  const [consumerSignature, setConsumerSignature] = useState("");
  const [crpSignature, setCrpSignature] = useState("");
  const [wvdrsSignature, setWvdrsSignature] = useState("");

  // Signature refs
  const consumerSigRef = useRef(null);
  const crpSigRef = useRef(null);
  const wvdrsSigRef = useRef(null);

  // Modal states for viewing pre-printed responsibilities
  const [showJobSearchModal, setShowJobSearchModal] = useState(false);
  const [showJobPlacementModal, setShowJobPlacementModal] = useState(false);

  // Pre-printed responsibilities from CRP-30 form
  const jobSearchResponsibilities = [
    "A. To demonstrate a positive attitude about job seeking as evidenced by: Timely follow through on job leads; maximum time and energy devoted to job search.",
    "B. With the Job Placement Specialist review and complete the Job Search Data Sheet (CRP 33).",
    "C. To discuss problems affecting job seeking with Job Placement Specialist.",
    "D. Maintain contact with Job Placement Specialist at minimum weekly.",
  ];

  const jobPlacementResponsibilities = [
    "A. To discuss problems affecting work performance with the Job Placement Specialist.",
    "B. To agree not to quit a job before discussing the situation with the Job Placement Specialist.",
    "C. To demonstrate acceptable worker traits on the job, especially attendance and punctuality.",
  ];

  // Load consumers
  useEffect(() => {
    if (!tenantId || !user?.user_id || !user?.role || !user?.tenant_type)
      return;

    secureAxios
      .get(`${apiBase}/api/consumers`, {
        params: {
          tenant_id: tenantId,
          user_id: user.user_id,
          role: user.role,
          tenant_type: user.tenant_type,
        },
      })
      .then((res) => {
        const activeConsumers = res.data.filter(
          (consumer) =>
            consumer.status && consumer.status.toLowerCase() === "active"
        );
        setConsumers(activeConsumers);
      })
      .catch((err) => {
        console.error("Ã¢ÂÅ’ Error fetching consumers:", err);
        toast.error("Failed to load consumers");
      });
  }, [tenantId, user, apiBase]);

  // Populate form when editing
  useEffect(() => {
    if (plan) {
      setFormData({
        consumer_id: plan.consumer_id || "",
        date: plan.date?.split("T")[0] || today,
        skills: plan.skills || "",
        vocational_goal: plan.vocational_goal || "",
        consumer_input: plan.consumer_input || "",
        counselor_input: plan.counselor_input || "",
        consumer_signature_date:
          plan.consumer_signature_date?.split("T")[0] || today,
        crp_signature_date: plan.crp_signature_date?.split("T")[0] || today,
        wvdrs_signature_date: plan.wvdrs_signature_date?.split("T")[0] || today,
        job_titles:
          plan.job_titles?.length > 0
            ? plan.job_titles
            : [{ job_title: "", availability_in_community: "" }],
        support_needs:
          plan.support_needs?.length > 0 ? plan.support_needs : [""],
        job_search_notes:
          plan.job_search_notes?.length > 0 ? plan.job_search_notes : [""],
        job_placement_notes:
          plan.job_placement_notes?.length > 0
            ? plan.job_placement_notes
            : [""],
      });

      // Reset signatures first, then load after delay
      setShowSignatures(false);
      setConsumerSignature("");
      setCrpSignature("");
      setWvdrsSignature("");
    } else {
      setFormData(initialFormState);
      setShowSignatures(true); // Show immediately for new forms
      setConsumerSignature("");
      setCrpSignature("");
      setWvdrsSignature("");
    }
  }, [plan]);

  // Show signature fields after a delay to ensure proper initialization (for edit mode)
  useEffect(() => {
    if (plan) {
      const timer = setTimeout(() => {
        setShowSignatures(true);
        // Set signatures after showing the fields
        setTimeout(() => {
          setConsumerSignature(plan.consumer_signature || "");
          setCrpSignature(plan.crp_signature || "");
          setWvdrsSignature(plan.wvdrs_signature || "");
        }, 100);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [
    plan?.consumer_signature,
    plan?.crp_signature,
    plan?.wvdrs_signature,
    plan?.job_placement_id,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (index, value, field, subField = null) => {
    setFormData((prev) => {
      const updated = [...prev[field]];
      if (subField) {
        updated[index] = { ...updated[index], [subField]: value };
      } else {
        updated[index] = value;
      }
      return { ...prev, [field]: updated };
    });
  };

  const addField = (field, defaultValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], defaultValue],
    }));
  };

  const removeField = (field, index) => {
    setFormData((prev) => {
      const updated = [...prev[field]];
      if (updated.length > 1) {
        updated.splice(index, 1);
      }
      return { ...prev, [field]: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (!formData.consumer_id) {
      toast.error("Please select a consumer");
      setLoading(false);
      return;
    }

    // Get current signatures - prioritize state, then check refs
    let finalConsumerSig = consumerSignature;
    let finalCrpSig = crpSignature;
    let finalWvdrsSig = wvdrsSignature;

    // If no signature in state but ref has content, use ref
    if (!finalConsumerSig && consumerSigRef.current) {
      finalConsumerSig = consumerSigRef.current.getCurrentSignature?.() || "";
    }
    if (!finalCrpSig && crpSigRef.current) {
      finalCrpSig = crpSigRef.current.getCurrentSignature?.() || "";
    }
    if (!finalWvdrsSig && wvdrsSigRef.current) {
      finalWvdrsSig = wvdrsSigRef.current.getCurrentSignature?.() || "";
    }

    // If still no signature, use the original plan signature (for edits)
    if (!finalConsumerSig && plan)
      finalConsumerSig = plan.consumer_signature || "";
    if (!finalCrpSig && plan) finalCrpSig = plan.crp_signature || "";
    if (!finalWvdrsSig && plan) finalWvdrsSig = plan.wvdrs_signature || "";

    const payload = {
      ...formData,
      tenant_id: tenantId,
      created_by: userId,
      consumer_signature: finalConsumerSig,
      crp_signature: finalCrpSig,
      wvdrs_signature: finalWvdrsSig,
    };

    try {
      let response;
      let jobPlacementId;

      if (plan) {
        response = await secureAxios.put(
          `${apiBase}/api/job-placements/${plan.job_placement_id}`,
          payload
        );
        jobPlacementId = plan.job_placement_id;
        toast.success("Job Placement Plan updated successfully");
      } else {
        response = await secureAxios.post(
          `${apiBase}/api/job-placements`,
          payload
        );
        jobPlacementId = response.data.job_placement_id;
        toast.success("Job Placement Plan created successfully");
      }

      try {
        const pdfRes = await secureAxios.get(
          `${apiBase}/api/job-placements/pdf/${jobPlacementId}`,
          { params: { tenant_id: tenantId } }
        );
        if (pdfRes.data?.pdf_url) {
          window.open(pdfRes.data.pdf_url, "_blank");
        }
      } catch (pdfErr) {
        console.error("PDF generation error:", pdfErr);
        toast.warning("Plan saved but PDF could not be generated");
      }

      onSave?.();
    } catch (err) {
      console.error("Ã¢ÂÅ’ Save failed:", err);
      toast.error(err.response?.data?.error || "Failed to save plan");
      setMessage("Failed to save plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h5 className="mb-2 mb-md-3 h6">{plan ? "Edit" : "Create"} Job Placement Plan</h5>

      {message && <p className="fw-bold text-danger">{message}</p>}

      {/* Consumer and Date Row */}
      <div className="row g-2 g-md-3">
        <div className="col-12 col-md-6 mb-2 mb-md-3">
          <label className="form-label">
            Consumer <span className="text-danger">*</span>
          </label>
          <select
            name="consumer_id"
            className="form-select form-select-sm"
            value={formData.consumer_id}
            onChange={handleChange}
            required
            disabled={!!plan}
          >
            <option value="">-- Select Consumer --</option>
            {consumers.map((c) => (
              <option key={c.consumer_id} value={c.consumer_id}>
                {c.name}
              </option>
            ))}
          </select>
          {plan && (
            <small className="text-muted">
              Consumer cannot be changed after creation
            </small>
          )}
        </div>

        <div className="col-12 col-md-6 mb-2 mb-md-3">
          <label className="form-label">Date</label>
          <input
            type="date"
            name="date"
            className="form-control form-control-sm"
            value={formData.date}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Skills Section */}
      <div className="mb-3">
        <label className="form-label">
          <strong>Specific Skills, Aptitudes, Interests, Abilities</strong>
        </label>
        <textarea
          name="skills"
          className="form-control form-control-sm"
          rows="3"
          value={formData.skills}
          onChange={handleChange}
          placeholder="Enter consumer's specific skills, aptitudes, interests, and abilities..."
        />
      </div>

      {/* Vocational Goal */}
      <div className="mb-3">
        <label className="form-label">
          <strong>Vocational Goal</strong>
        </label>
        <textarea
          name="vocational_goal"
          className="form-control form-control-sm"
          rows="2"
          value={formData.vocational_goal}
          onChange={handleChange}
          placeholder="Enter the vocational goal..."
        />
      </div>

      {/* Job Titles */}
      <div className="mb-3">
        <label className="form-label">
          <strong>Job Titles</strong>
        </label>
        {formData.job_titles.map((item, idx) => (
          <div key={idx} className="card mb-2">
            <div className="card-body p-2">
              <div className="row g-2">
                <div className="col-md-5">
                  <label className="form-label small mb-1">Job Title</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Enter job title..."
                    value={item.job_title}
                    onChange={(e) =>
                      handleArrayChange(
                        idx,
                        e.target.value,
                        "job_titles",
                        "job_title"
                      )
                    }
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small mb-1">
                    Availability in Community
                  </label>
                  <textarea
                    className="form-control form-control-sm"
                    placeholder="Describe availability in the community..."
                    value={item.availability_in_community}
                    onChange={(e) =>
                      handleArrayChange(
                        idx,
                        e.target.value,
                        "job_titles",
                        "availability_in_community"
                      )
                    }
                    rows="2"
                    style={{ resize: "vertical" }}
                  />
                </div>
                <div className="col-md-1 d-flex align-items-end">
                  {formData.job_titles.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removeField("job_titles", idx)}
                      title="Remove"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() =>
            addField("job_titles", {
              job_title: "",
              availability_in_community: "",
            })
          }
        >
          <i className="bi bi-plus-lg me-1"></i>
          Add Job Title
        </button>
      </div>

      {/* Support Needs */}
      <div className="mb-3">
        <label className="form-label">
          <strong>Support Needs</strong>
          <small className="text-muted ms-2">
            (i.e. child care, transportation, housing, etc.)
          </small>
        </label>
        {formData.support_needs.map((val, idx) => (
          <div key={idx} className="d-flex mb-2 gap-2">
            <span className="input-group-text">{idx + 1}.</span>
            <textarea
              className="form-control form-control-sm"
              value={val}
              onChange={(e) =>
                handleArrayChange(idx, e.target.value, "support_needs")
              }
              placeholder="Enter support need..."
              rows="2"
              style={{ resize: "vertical" }}
            />
            {formData.support_needs.length > 1 && (
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => removeField("support_needs", idx)}
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => addField("support_needs", "")}
        >
          <i className="bi bi-plus-lg me-1"></i>
          Add Support Need
        </button>
      </div>

      {/* Consumer Responsibilities - Job Search */}
      <div className="mb-3">
        <label className="form-label d-flex align-items-center">
          <strong>Consumer Responsibilities - I. Job Search</strong>
          <button
            type="button"
            className="btn btn-outline-info btn-sm ms-2"
            onClick={() => setShowJobSearchModal(true)}
            title="View pre-printed responsibilities (A-D)"
          >
            <i className="bi bi-info-circle me-1"></i>
            View A-D
          </button>
        </label>
        <p className="text-muted small mb-2">
          Standard responsibilities (A-D) are pre-printed on the form. Add
          additional items below:
        </p>
        {formData.job_search_notes.map((val, idx) => (
          <div key={idx} className="d-flex mb-2 gap-2">
            <span className="input-group-text">
              {String.fromCharCode(69 + idx)}.
            </span>
            <textarea
              className="form-control form-control-sm"
              value={val}
              onChange={(e) =>
                handleArrayChange(idx, e.target.value, "job_search_notes")
              }
              placeholder="Additional job search responsibility..."
              rows="2"
              style={{ resize: "vertical" }}
            />
            {formData.job_search_notes.length > 1 && (
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => removeField("job_search_notes", idx)}
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => addField("job_search_notes", "")}
        >
          <i className="bi bi-plus-lg me-1"></i>
          Add Job Search Note
        </button>
      </div>

      {/* Job Search Responsibilities Modal */}
      {showJobSearchModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">
                  <i className="bi bi-clipboard-check me-2"></i>
                  Consumer Responsibilities - I. Job Search (Pre-printed on
                  CRP-30)
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowJobSearchModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">
                  <i className="bi bi-info-circle me-1"></i>
                  These responsibilities are pre-printed on the official CRP-30
                  form. Use this as a checklist during your meeting.
                </p>
                <ul className="list-group">
                  {jobSearchResponsibilities.map((item, idx) => (
                    <li key={idx} className="list-group-item">
                      <i className="bi bi-check-square text-success me-2"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowJobSearchModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consumer Responsibilities - Job Placement */}
      <div className="mb-3">
        <label className="form-label d-flex align-items-center">
          <strong>Consumer Responsibilities - II. Job Placement</strong>
          <button
            type="button"
            className="btn btn-outline-info btn-sm ms-2"
            onClick={() => setShowJobPlacementModal(true)}
            title="View pre-printed responsibilities (A-C)"
          >
            <i className="bi bi-info-circle me-1"></i>
            View A-C
          </button>
        </label>
        <p className="text-muted small mb-2">
          Standard responsibilities (A-C) are pre-printed on the form. Add
          additional items below:
        </p>
        {formData.job_placement_notes.map((val, idx) => (
          <div key={idx} className="d-flex mb-2 gap-2">
            <span className="input-group-text">
              {String.fromCharCode(68 + idx)}.
            </span>
            <textarea
              className="form-control form-control-sm"
              value={val}
              onChange={(e) =>
                handleArrayChange(idx, e.target.value, "job_placement_notes")
              }
              placeholder="Additional job placement responsibility..."
              rows="2"
              style={{ resize: "vertical" }}
            />
            {formData.job_placement_notes.length > 1 && (
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => removeField("job_placement_notes", idx)}
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => addField("job_placement_notes", "")}
        >
          <i className="bi bi-plus-lg me-1"></i>
          Add Job Placement Note
        </button>
      </div>

      {/* Job Placement Responsibilities Modal */}
      {showJobPlacementModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">
                  <i className="bi bi-clipboard-check me-2"></i>
                  Consumer Responsibilities - II. Job Placement (Pre-printed on
                  CRP-30)
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowJobPlacementModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">
                  <i className="bi bi-info-circle me-1"></i>
                  These responsibilities are pre-printed on the official CRP-30
                  form. Use this as a checklist during your meeting.
                </p>
                <ul className="list-group">
                  {jobPlacementResponsibilities.map((item, idx) => (
                    <li key={idx} className="list-group-item">
                      <i className="bi bi-check-square text-success me-2"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowJobPlacementModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Duration */}
      <div className="alert alert-secondary mb-2 mb-md-3 py-2 small">
        <h6 className="alert-heading mb-1">
          <i className="bi bi-calendar-range me-2"></i>
          <strong>PLAN DURATION:</strong>
        </h6>
        <p className="mb-0 small">
          This plan remains in effect, with any modifications, until job
          placement closure has been achieved.
        </p>
      </div>

      {/* Additional Input Sections */}
      <div className="row g-2 g-md-3">
        <div className="col-12 col-md-6 mb-2 mb-md-3">
          <label className="form-label">
            <strong>Additional Consumer Input</strong>
          </label>
          <textarea
            name="consumer_input"
            className="form-control form-control-sm"
            rows="3"
            value={formData.consumer_input}
            onChange={handleChange}
            placeholder="Additional input from the consumer..."
          />
        </div>

        <div className="col-12 col-md-6 mb-2 mb-md-3">
          <label className="form-label">
            <strong>Additional Counselor Input</strong>
          </label>
          <textarea
            name="counselor_input"
            className="form-control form-control-sm"
            rows="3"
            value={formData.counselor_input}
            onChange={handleChange}
            placeholder="Additional input from the counselor..."
          />
        </div>
      </div>

      {/* Signature Section */}
      <div className="alert alert-info mb-2 mb-md-3 py-2 small">
        <h6 className="alert-heading mb-1">
          <i className="bi bi-pen me-2"></i>
          Digital Signature Requirements
        </h6>
        <p className="mb-0 small">
          The signatures below serve as legal authorization for this plan. By
          signing, each party acknowledges they have reviewed and agree to the
          plan contents.
        </p>
      </div>

      <div className="row">
        {/* Consumer Signature */}
        <div className="col-12 col-sm-6 col-md-4 mb-2 mb-md-3">
          <label className="form-label">Consumer Signature</label>
          <div
            className="border rounded p-2 bg-white"
            style={{ minHeight: "180px" }}
          >
            {showSignatures ? (
              <SignatureField
                ref={consumerSigRef}
                value={consumerSignature}
                onChange={setConsumerSignature}
                penColor="blue"
                label=""
              />
            ) : (
              <div
                style={{
                  height: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span className="text-muted">Loading signature...</span>
              </div>
            )}
          </div>
          <div className="mt-2">
            <label className="form-label small">Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              name="consumer_signature_date"
              value={formData.consumer_signature_date}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* CRP Signature */}
        <div className="col-12 col-sm-6 col-md-4 mb-2 mb-md-3">
          <label className="form-label">CRP Placement Specialist</label>
          <div
            className="border rounded p-2 bg-white"
            style={{ minHeight: "180px" }}
          >
            {showSignatures ? (
              <SignatureField
                ref={crpSigRef}
                value={crpSignature}
                onChange={setCrpSignature}
                penColor="blue"
                label=""
              />
            ) : (
              <div
                style={{
                  height: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span className="text-muted">Loading signature...</span>
              </div>
            )}
          </div>
          <div className="mt-2">
            <label className="form-label small">Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              name="crp_signature_date"
              value={formData.crp_signature_date}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* WVDRS Signature */}
        <div className="col-12 col-sm-6 col-md-4 mb-2 mb-md-3">
          <label className="form-label">WVDRS Counselor</label>
          <div
            className="border rounded p-2 bg-white"
            style={{ minHeight: "180px" }}
          >
            {showSignatures ? (
              <SignatureField
                ref={wvdrsSigRef}
                value={wvdrsSignature}
                onChange={setWvdrsSignature}
                penColor="blue"
                label=""
              />
            ) : (
              <div
                style={{
                  height: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span className="text-muted">Loading signature...</span>
              </div>
            )}
          </div>
          <div className="mt-2">
            <label className="form-label small">Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              name="wvdrs_signature_date"
              value={formData.wvdrs_signature_date}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <hr />
      <div className="d-flex gap-2">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1"></i>
              {plan ? "Update Plan" : "Create Plan"}
            </>
          )}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default JobPlacementForm;